import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { 
  Popconfirm, Divider, Card, Select, Input, Button, Switch, InputNumber, Space, 
  Typography, Radio, Table, Progress, message, Modal,
  Collapse, Spin, Checkbox, Alert, Tag, Tooltip, Tabs
} from 'antd';
import { 
  DeleteOutlined, InfoCircleOutlined, PlusOutlined, 
  MinusOutlined, ReloadOutlined, EyeOutlined, DownloadOutlined,
  SettingOutlined, GlobalOutlined 
} from '@ant-design/icons';
import NodeInputHandler from '../common/NodeInputHandler';
import { automatedInstance } from '../api/axiosConfig';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const FirecrawlNode = ({ id, data, isConnectable }) => {
  // Basic node state
  const [nodeName, setNodeName] = useState(data.nodeName || `Firecrawl ${id}`);
  const [operation, setOperation] = useState(data.operation || 'scrape');
  const [url, setUrl] = useState(data.url || '');
  
  // Scrape options state
  const [format, setFormat] = useState(data.format || 'markdown');
  const [onlyMainContent, setOnlyMainContent] = useState(data.onlyMainContent !== false);
  const [includeTags, setIncludeTags] = useState(data.includeTags || []);
  const [excludeTags, setExcludeTags] = useState(data.excludeTags || []);
  const [waitFor, setWaitFor] = useState(data.waitFor || 0);
  const [timeout, setTimeout] = useState(data.timeout || 30000);
  const [isMobile, setIsMobile] = useState(data.isMobile || false);

  // Extract-specific state
  const [extractType, setExtractType] = useState(data.extractType || 'prompt');
  const [extractPrompt, setExtractPrompt] = useState(data.extractPrompt || '');
  const [extractFields, setExtractFields] = useState(data.extractFields || []);

  // Crawl-specific state
  const [maxDepth, setMaxDepth] = useState(data.maxDepth || 2);
  const [ignoreSitemap, setIgnoreSitemap] = useState(data.ignoreSitemap !== false);
  const [limit, setLimit] = useState(data.limit || 10);
  const [allowBackwardLinks, setAllowBackwardLinks] = useState(data.allowBackwardLinks || false);
  const [allowExternalLinks, setAllowExternalLinks] = useState(data.allowExternalLinks || false);

  // Execution and results state
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [crawlResults, setCrawlResults] = useState(data.crawlResults || []);
  const [selectedPages, setSelectedPages] = useState(data.selectedPages || []);
  const [lastExecutionTime, setLastExecutionTime] = useState(data.lastExecutionTime || null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [activeTab, setActiveTab] = useState('input');

  // Update node data when state changes
  useEffect(() => {
    data.updateNodeData(id, {
      nodeName,
      operation,
      url,
      format,
      onlyMainContent,
      includeTags,
      excludeTags,
      waitFor,
      timeout,
      isMobile,
      extractType,
      extractPrompt,
      extractFields,
      maxDepth,
      ignoreSitemap,
      limit,
      allowBackwardLinks,
      allowExternalLinks,
      crawlResults,
      selectedPages,
      lastExecutionTime,
      crawlResults,
      selectedPages,
      lastExecutionTime
    });
  }, [
    nodeName, operation, url, format, onlyMainContent,
    includeTags, excludeTags, waitFor, timeout, isMobile,
    extractType, extractPrompt, extractFields,
    maxDepth, ignoreSitemap, limit, allowBackwardLinks,
    allowExternalLinks, crawlResults, selectedPages,
    lastExecutionTime
  ]);

  useEffect(() => {
    if (data.crawlResults) {
      setCrawlResults(data.crawlResults);
    }
    if (data.selectedPages) {
      setSelectedPages(data.selectedPages);
    }
    if (data.lastExecutionTime) {
      setLastExecutionTime(data.lastExecutionTime);
    }
  }, []);

  const CrawlingWarning = () => (
    <Alert
      message="Crawling Configuration Warning"
      description={
        <div>
          <p>Please configure crawling parameters carefully:</p>
          <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
            <li>Large depth and high page limits can generate very large outputs</li>
            <li>Processing large amounts of text may cause workflow timeouts</li>
            <li>Consider using pagination or filtering to manage content size</li>
          </ul>
        </div>
      }
      type="warning"
      showIcon
      style={{ marginBottom: 16 }}
    />
  );

  // Render methods can be simplified by breaking them into smaller components
  const renderUrlInput = () => (
    <div>
      <Space align="center" style={{ marginBottom: 4 }}>
        <Text strong>URL</Text>
        <Tooltip title="Enter the URL to scrape or crawl">
          <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
        </Tooltip>
      </Space>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Input
          placeholder="Enter URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="nodrag"
          style={{ width: '100%' }}
        />
        <div>
          <NodeInputHandler 
            nodeId={id} 
            onInsert={(value) => setUrl(prevUrl => prevUrl + value)}
          />
        </div>
      </div>
    </div>
  );


  const handleExecute = async () => {
    if (!url) {
      message.error('Please enter a URL');
      return;
    }
  
    setIsProcessing(true);
    setProgress(0);
  
    try {
      const payload = {
        operation,
        url,
        scrapeOptions: {
          formats: [format],
          onlyMainContent,
          includeTags: includeTags.filter(Boolean),
          excludeTags: excludeTags.filter(Boolean),
          waitFor,
          timeout,
          mobile: isMobile
        }
      };
  
      // Add extract options for scrape operation
      if (operation === 'scrape' && format === 'extract') {
        payload.scrapeOptions.extract = {
          type: extractType,
          ...(extractType === 'prompt' 
            ? { prompt: extractPrompt }
            : { fields: extractFields }
          )
        };
      }
  
      if (operation === 'crawl') {
        payload.crawlOptions = {
          maxDepth,
          ignoreSitemap,
          limit,
          allowBackwardLinks,
          allowExternalLinks
        };
      }
  
      console.log("Making request to firecrawl execute:", payload);
  
      const response = await automatedInstance.post(
        `${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/firecrawl/execute`,
        payload,
        {
          headers: {
            'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce,
            'Content-Type': 'application/json'
          }
        }
      );
  
      if (operation === 'crawl') {
        // Start polling for progress
        const pollId = setInterval(async () => {
          try {
            const progressResponse = await automatedInstance.get(
              `${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/firecrawl/progress/${response.data.jobId}`,
              {
                headers: {
                  'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce
                }
              }
            );
            
            const progress = progressResponse.data;
            setProgress(progress.progress || 0);
            
            if (progress.status === 'completed') {
              clearInterval(pollId);
              
              // Extract the data from the response structure
              const responseData = progressResponse.data;
              console.log('Response data:', responseData);
            
              // Get the crawl data, ensuring we handle both array and object responses
              let crawlData = [];
              if (responseData.success) {
                // Handle case where data is in results array
                if (Array.isArray(responseData.results)) {
                  crawlData = responseData.results;
                }
                // Handle case where data is in data field
                else if (Array.isArray(responseData.data)) {
                  crawlData = responseData.data;
                }
                // Handle case where the entire response is the data
                else if (Array.isArray(responseData)) {
                  crawlData = responseData;
                }
              }
            
              console.log('Processed crawl data:', crawlData);
              
              // Format the crawl results
              const formattedResults = crawlData.map(result => {
                return {
                  url: result.url || '',
                  content: result.content || result.markdown || '',
                  contentLength: (result.content || result.markdown || '').length,
                  timestamp: new Date().toISOString(),
                  metadata: {
                    title: result.title || '',
                    description: result.description || '',
                    ...result.metadata
                  }
                };
              });
              
              console.log('Formatted crawl results:', formattedResults);
              
              if (formattedResults.length > 0) {
                setCrawlResults(formattedResults);
                setLastExecutionTime(new Date().toISOString());
                setIsProcessing(false);
                message.success(`Crawling completed successfully (${formattedResults.length} pages found)`);
              } else {
                setIsProcessing(false);
                message.warning('No pages were crawled');
              }
            } else if (progress.status === 'error') {
              clearInterval(pollId);
              setIsProcessing(false);
              message.error(progress.error || 'Crawling failed');
            }
          } catch (error) {
            console.error('Progress polling error:', error);
            // Only clear interval and show error if it's not a temporary network issue
            if (error.response && error.response.status !== 503) {
              clearInterval(pollId);
              setIsProcessing(false);
              message.error('Error checking progress: ' + (error.response?.data?.message || error.message));
            }
          }
        }, 2000); // Increased polling interval to 2 seconds to reduce server load
      } else {
        // Handle single page scrape
        const content = response.data.content || response.data;
        setCrawlResults([{
          url: url,
          content: content,
          contentLength: content.length,
          timestamp: new Date().toISOString()
        }]);
        setLastExecutionTime(new Date().toISOString());
        setIsProcessing(false);
        message.success('Scraping completed successfully');
      }
    } catch (error) {
      console.error('Firecrawl execution error:', error);
      setIsProcessing(false);
      message.error('Operation failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const renderPreviewContent = () => {
    if (!previewContent) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
        </div>
      );
    }
  
    let contentToDisplay;
    try {
      // If it's a JSON string, parse and re-stringify it with formatting
      contentToDisplay = typeof previewContent === 'string' 
        ? JSON.stringify(JSON.parse(previewContent), null, 2)
        : JSON.stringify(previewContent, null, 2);
    } catch (e) {
      // If it's not JSON, display as is
      contentToDisplay = previewContent.toString();
    }
  
    if (format === 'html' || format === 'rawHtml') {
      return (
        <iframe
          srcDoc={contentToDisplay}
          style={{
            width: '100%',
            height: '500px',
            border: 'none',
            backgroundColor: 'white'
          }}
          title="HTML Preview"
        />
      );
    }
  
    return (
      <div 
        style={{ 
          maxHeight: '500px', 
          overflow: 'auto', 
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px'
        }}
      >
        <pre style={{ 
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          margin: 0
        }}>
          {contentToDisplay}
        </pre>
      </div>
    );
  };

  // Also update the preview handler to better handle the content
  const handlePreview = (record) => {
    let contentToPreview;
    
    if (format === 'markdown') {
      contentToPreview = record.content || 'No content available';
    } else {
      // For metadata view, show formatted JSON
      try {
        const metadata = typeof record.metadata === 'string' 
          ? JSON.parse(record.metadata) 
          : record.metadata;
        contentToPreview = JSON.stringify(metadata, null, 2);
      } catch (e) {
        contentToPreview = record.content || 'No content available';
      }
    }
  
    setPreviewContent(contentToPreview);
    setPreviewUrl(record.url);
    setShowPreviewModal(true);
  };

  const handleDownload = (record) => {
    const content = format === 'markdown' ? record.content : JSON.stringify(record.metadata, null, 2);
    const extension = format === 'markdown' ? 'md' : 'json';
    
    const blob = new Blob([content], { 
      type: format === 'markdown' ? 'text/markdown' : 'application/json' 
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${record.url.replace(/[^a-z0-9]/gi, '_')}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      width: '40%',
      render: (text, record) => (
        <a href={text} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      )
    },
    {
      title: 'Title',
      key: 'title',
      width: '30%',
      render: (_, record) => {
        const metadata = record.metadata || {};
        return metadata.title || 'No title';
      }
    },
    {
      title: 'Size',
      key: 'contentLength',
      width: '10%',
      render: (_, record) => {
        const bytes = record.contentLength || 0;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        let i = 0;
        let size = bytes;
        while (size >= 1024 && i < sizes.length - 1) {
          size /= 1024;
          i++;
        }
        return `${Math.round(size * 100) / 100} ${sizes[i]}`;
      },
    },
    {
      title: 'Actions',
      key: 'action',
      width: '20%',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handlePreview(record)}
            title="Preview Content"
          />
          <Button
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => handleDownload(record)}
            title="Download Content"
          />
          <Checkbox
            checked={selectedPages.includes(record.url)}
            onChange={(e) => {
              const newSelected = e.target.checked
                ? [...selectedPages, record.url]
                : selectedPages.filter(url => url !== record.url);
              setSelectedPages(newSelected);
            }}
          />
        </Space>
      ),
    },
  ];
  

  


  const rowSelection = operation === 'crawl' ? {
    selectedRowKeys: selectedPages,
    onChange: (selectedRowKeys) => {
      setSelectedPages(selectedRowKeys);
    },
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_NONE
    ]
  } : undefined;

  const renderScrapeOptions = () => (
    <Collapse defaultActiveKey={['scrapeOptions']} ghost>
      <Panel header="Scrape Options" key="scrapeOptions">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            style={{ width: '100%' }}
            value={format}
            onChange={setFormat}
            className="nodrag"
          >
            <Option value="markdown">Markdown</Option>
            <Option value="html">HTML</Option>
            <Option value="rawHtml">Raw HTML</Option>
            <Option value="links">Links</Option>
            <Option value="screenshot">Screenshot</Option>
            {operation === 'scrape' && <Option value="extract">Extract</Option>}
          </Select>

          {operation === 'scrape' && format === 'extract' ? (
            <>
              <Radio.Group 
                onChange={(e) => setExtractType(e.target.value)} 
                value={extractType} 
                className="nodrag"
              >
                <Radio value="prompt">Prompt</Radio>
                <Radio value="schema">Schema</Radio>
              </Radio.Group>

              {extractType === 'prompt' ? (
                <TextArea
                  placeholder="Enter extraction prompt"
                  value={extractPrompt}
                  onChange={(e) => setExtractPrompt(e.target.value)}
                  className="nodrag"
                  rows={3}
                />
              ) : (
                <div className="extract-fields">
                {extractFields.map((field, index) => (
                    <Space key={index} style={{ marginBottom: 8 }}>
                        <Input
                            placeholder="Field name"
                            value={field.name}
                            onChange={(e) => handleExtractFieldChange(index, field, e.target.value)}
                            className="nodrag"
                        />
                        <Select
                            value={field.type}
                            onChange={(value) => {
                                const newFields = [...extractFields];
                                newFields[index] = { ...field, type: value };
                                setExtractFields(newFields);
                            }}
                            className="nodrag"
                        >
                            <Option value="string">String</Option>
                            <Option value="number">Number</Option>
                            <Option value="boolean">Boolean</Option>
                            <Option value="array">Array</Option>
                        </Select>
                        <Button 
                            icon={<MinusOutlined />} 
                            onClick={() => {
                                const newFields = extractFields.filter((_, i) => i !== index);
                                setExtractFields(newFields);
                            }}
                            className="nodrag"
                        />
                    </Space>
                ))}
                <Button 
                    icon={<PlusOutlined />} 
                    onClick={() => setExtractFields([...extractFields, { name: '', type: 'string' }])}
                    className="nodrag"
                >
                    Add Field
                </Button>
            </div>
              )}
              <Divider />
            </>
          ) : (
            <>
              <Switch
                checked={onlyMainContent}
                onChange={setOnlyMainContent}
                checkedChildren="Only Main Content"
                unCheckedChildren="Full Content"
                className="nodrag"
              />

              <Switch
                checked={isMobile}
                onChange={setIsMobile}
                checkedChildren="Mobile View"
                unCheckedChildren="Desktop View"
                className="nodrag"
              />

              <Input
                placeholder="Include Tags (comma-separated)"
                value={includeTags.join(',')}
                onChange={(e) => setIncludeTags(e.target.value.split(',').map(tag => tag.trim()))}
                className="nodrag"
              />

              <Input
                placeholder="Exclude Tags (comma-separated)"
                value={excludeTags.join(',')}
                onChange={(e) => setExcludeTags(e.target.value.split(',').map(tag => tag.trim()))}
                className="nodrag"
              />
            </>
          )}

          <InputNumber
            style={{ width: '100%' }}
            placeholder="Wait For (ms)"
            value={waitFor}
            onChange={setWaitFor}
            className="nodrag"
          />

          <InputNumber
            style={{ width: '100%' }}
            placeholder="Timeout (ms)"
            value={timeout}
            onChange={setTimeout}
            className="nodrag"
          />
        </Space>
      </Panel>
    </Collapse>
  );


  const handleExtractFieldChange = (index, field, value) => {
    const newFields = [...extractFields];
    newFields[index] = { 
      ...field, 
      name: value.toLowerCase() // Force lowercase
    };
    setExtractFields(newFields);
    
    // Add validation message
    if (value !== value.toLowerCase()) {
      message.info('Field names are automatically converted to lowercase for consistency');
    }
  };

  const renderCrawlOptions = () => (
    <Collapse ghost>
      <Panel header="Crawl Options" key="crawlOptions">
        <Space direction="vertical" style={{ width: '100%' }}>
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Max Depth"
            value={maxDepth}
            onChange={setMaxDepth}
            min={1}
            className="nodrag"
          />

          <InputNumber
            style={{ width: '100%' }}
            placeholder="Page Limit"
            value={limit}
            onChange={setLimit}
            min={1}
            className="nodrag"
          />

          <Switch
            checked={ignoreSitemap}
            onChange={setIgnoreSitemap}
            checkedChildren="Ignore Sitemap"
            unCheckedChildren="Use Sitemap"
            className="nodrag"
          />

          <Switch
            checked={allowBackwardLinks}
            onChange={setAllowBackwardLinks}
            checkedChildren="Allow Backward Links"
            unCheckedChildren="No Backward Links"
            className="nodrag"
          />

          <Switch
            checked={allowExternalLinks}
            onChange={setAllowExternalLinks}
            checkedChildren="Allow External Links"
            unCheckedChildren="No External Links"
            className="nodrag"
          />
        </Space>
      </Panel>
    </Collapse>
  );

  return (
    <Card 
      title={
        <Space>
          <GlobalOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>
          <span>Web Scraper</span>
          <Tag color="blue">Firecrawl</Tag>
        </Space>
      }
      style={{ width: 380 }}
      extra={
        <Space>
          <Button 
            icon={<SettingOutlined />}
            type="text"
            onClick={() => setActiveTab('settings')}
          />
      <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
        </Space>
      }
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={(e) => setNodeName(e.target.value)}
        style={{ marginBottom: 16 }}
        prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
        className="nodrag"
      />

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Input" key="input">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              style={{ width: '100%', marginBottom: 16 }}
              value={operation}
              onChange={setOperation}
              className="nodrag"
            >
              <Option value="scrape">Single Page Scrape</Option>
              <Option value="crawl">Website Crawler</Option>
            </Select>

            {renderUrlInput()}

            {operation === 'crawl' && <CrawlingWarning />}

            {operation === 'scrape' && (
              <Button
                type="primary"
                icon={<ReloadOutlined spin={isProcessing} />}
                onClick={handleExecute}
                loading={isProcessing}
                style={{ width: '100%' }}
                disabled={!url}
              >
                {isProcessing ? 'Processing...' : 'Scrape Now'}
              </Button>
            )}

            {isProcessing && (
              <Progress percent={progress} status="active" />
            )}
          </Space>
        </TabPane>

        <TabPane tab="Settings" key="settings">
          <Space direction="vertical" style={{ width: '100%' }}>
            {operation === 'crawl' ? (
              // Show both options in crawl mode
              <>
                {renderScrapeOptions()}
                {renderCrawlOptions()}
              </>
            ) : (
              // Only show scrape options in scrape mode
              renderScrapeOptions()
            )}
          </Space>
        </TabPane>

        <TabPane tab="Results" key="results">
          <Space direction="vertical" style={{ width: '100%' }}>
            {crawlResults.length > 0 ? (
              <>
                <Text type="secondary">
                  Last updated: {new Date(lastExecutionTime).toLocaleString()}
                </Text>
                
                <Table
                  dataSource={crawlResults}
                  columns={columns}
                  size="small"
                  pagination={{ 
                    pageSize: 5,
                    size: "small",
                    hideOnSinglePage: true
                  }}
                  scroll={{ y: 200 }}
                  rowKey="url"
                  rowSelection={rowSelection}
                />

                {operation === 'crawl' && selectedPages.length > 0 && (
                  <Text type="secondary">
                    {selectedPages.length} page{selectedPages.length !== 1 ? 's' : ''} selected
                  </Text>
                )}
              </>
            ) : (
              <Alert
                message="No Results"
                description="Run a scrape operation to see results here"
                type="info"
                showIcon
              />
            )}
          </Space>
        </TabPane>

        <TabPane tab="Output" key="output">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              Available outputs from this node:
            </Text>
            <div style={{ 
              backgroundColor: '#f5f5f5', 
              padding: '12px', 
              borderRadius: '6px'
            }}>
              <Space direction="vertical">
                <div>
                  <Text strong>Scraped Content</Text>
                  <div>
                    <Text code>[Input from {id}]</Text>
                  </div>
                </div>
                {operation === 'crawl' && (
                  <div>
                    <Text strong>Selected Pages Content</Text>
                    <div>
                      <Text code>[[selected] from {id}]</Text>
                    </div>
                  </div>
                )}
              </Space>
            </div>
          </Space>
        </TabPane>
      </Tabs>

      <Modal
  title={
    <Space>
      <Text>Content Preview</Text>
      <Text type="secondary" style={{ fontSize: '0.9em' }}>
        {previewUrl}
      </Text>
    </Space>
  }
  visible={showPreviewModal}
  onCancel={() => setShowPreviewModal(false)}
  footer={[
    <Button 
      key="download" 
      icon={<DownloadOutlined />}
      onClick={() => handleDownload({ url: previewUrl, content: previewContent })}
    >
      Download
    </Button>,
    <Button 
      key="close" 
      onClick={() => setShowPreviewModal(false)}
    >
      Close
    </Button>
  ]}
  width={800}
>
  {renderPreviewContent()} {/* Use the renderPreviewContent function here */}
</Modal>

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
    </Card>
  );
};

export default FirecrawlNode;